import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from './LangContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import TechCarousel from './components/TechCarousel';
import CookieBanner from './CookieBanner';

function Consulting() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const services = t('consulting.services.items', { returnObjects: true });
  const sectors = t('consulting.sectors.items', { returnObjects: true });
  const process = t('consulting.process.items', { returnObjects: true });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();

  const [form, setForm] = useState({ company: '', email: '', challenge: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/send-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, _type: 'consulting' }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitStatus('success');
        setForm({ company: '', email: '', challenge: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', window.location.pathname);
  };

  return (
    <div className="min-h-screen bg-background text-on-background">
      <header className="fixed top-0 z-50 h-20 w-full border-b border-white/10 bg-zinc-950/80 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-6 md:px-8">
          <Link to={prefix + '/'} className="flex items-center gap-3" aria-label="ElmakinaTech">
            <img src="/elmakinatech-icon.svg" alt="" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tighter text-white">ElmakinaTech</span>
          </Link>
          <nav className="hidden items-center gap-6 md:flex lg:gap-8">
            <a onClick={(e) => { e.preventDefault(); navigate(prefix + '/', { state: { scrollTo: 'servicios' } }); }} className="font-label-caps text-label-caps text-zinc-400 transition-colors duration-200 hover:text-white cursor-pointer" href={prefix + '/'}>{t('landing.services')}</a>
            <Link className="font-label-caps text-label-caps text-cyan-400 transition-colors duration-200 hover:text-white" to={prefix + '/consultoria'}>{t('landing.consulting')}</Link>
            <a onClick={(e) => scrollTo(e, 'contacto')} className="font-label-caps text-label-caps text-zinc-400 transition-colors duration-200 hover:text-white cursor-pointer" href="#contacto">{t('landing.contact')}</a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <a onClick={(e) => scrollTo(e, 'contacto')} href="#contacto" className="hidden rounded bg-secondary-container px-5 py-3 font-label-caps text-label-caps text-on-secondary-fixed transition-all duration-300 hover:brightness-110 lg:inline-flex">
              {t('consulting.cta')}
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="inline-flex md:hidden items-center justify-center w-10 h-10 text-white"
              aria-label="Menú"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
                {menuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M3 12h18M3 6h18M3 18h18" />
                )}
              </svg>
            </button>
          </div>
          {menuOpen && (
            <div className="absolute top-full left-0 right-0 bg-zinc-950/95 backdrop-blur-md border-b border-white/10 md:hidden">
              <div className="flex flex-col px-6 py-6 gap-6">
                <a onClick={(e) => { setMenuOpen(false); e.preventDefault(); navigate(prefix + '/', { state: { scrollTo: 'servicios' } }); }} className="text-zinc-400 font-label-caps text-label-caps hover:text-white transition-colors cursor-pointer" href={prefix + '/'}>{t('landing.services')}</a>
                <Link onClick={() => setMenuOpen(false)} className="text-cyan-400 font-label-caps text-label-caps hover:text-white transition-colors" to={prefix + '/consultoria'}>{t('landing.consulting')}</Link>
                <a onClick={(e) => { setMenuOpen(false); scrollTo(e, 'contacto'); }} className="text-zinc-400 font-label-caps text-label-caps hover:text-white transition-colors cursor-pointer" href="#contacto">{t('landing.contact')}</a>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="pt-20">
        <section className="relative overflow-hidden border-b border-white/5 bg-background">
          <div className="absolute inset-0">
            <img
              className="h-full w-full object-cover opacity-20 grayscale mix-blend-screen"
              alt="Equipo de consultoria tecnologica trabajando con paneles de datos"
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1800&q=80"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-background via-background/90 to-cyan-900/25" />
          </div>
          <div className="relative z-10 mx-auto grid min-h-[420px] sm:min-h-[500px] md:min-h-[700px] max-w-7xl grid-cols-1 items-center gap-14 px-6 py-16 md:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:py-24">
            <div>
              <span className="mb-6 block font-label-caps text-label-caps text-cyan-400">{t('consulting.eyebrow')}</span>
              <h1 className="hero-title text-4xl sm:text-5xl md:text-6xl lg:text-display-xl text-white">
                {t('consulting.heroTitle')} <span className="text-cyan-400">{t('consulting.heroAccent')}</span>
              </h1>
              <p className="mt-8 max-w-2xl font-body-lg text-body-lg text-on-surface-variant">{t('consulting.heroDescription')}</p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <a onClick={(e) => scrollTo(e, 'contacto')} href="#contacto" className="rounded bg-secondary-container px-8 py-4 text-center font-label-caps text-label-caps text-on-secondary-fixed transition-all duration-300 hover:bg-white">
                  {t('consulting.primaryAction')}
                </a>
                <a onClick={(e) => scrollTo(e, 'servicios-consultoria')} href="#servicios-consultoria" className="inline-flex items-center justify-center gap-2 rounded border border-white/20 px-8 py-4 font-label-caps text-label-caps text-white transition-all duration-300 hover:bg-white/5">
                  {t('consulting.secondaryAction')}
                  <span className="material-symbols-outlined text-[18px]">arrow_downward</span>
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {t('consulting.metrics', { returnObjects: true }).map((metric) => (
                <div key={metric.label} className="glass-card min-h-[148px] rounded-lg p-6">
                  <span className="font-headline-lg text-4xl text-white">{metric.value}</span>
                  <p className="mt-4 font-label-caps text-[11px] text-zinc-400">{metric.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="servicios-consultoria" className="bg-surface-container-lowest py-section-padding">
          <div className="mx-auto max-w-7xl px-6 md:px-8">
            <div className="mb-14 flex flex-col justify-between gap-8 border-b border-white/10 pb-10 lg:flex-row lg:items-end">
              <div className="max-w-3xl">
                <span className="mb-4 block font-label-caps text-label-caps text-cyan-400">{t('consulting.services.eyebrow')}</span>
                <h2 className="font-headline-lg text-headline-lg text-white">{t('consulting.services.title')}</h2>
                <p className="mt-5 font-body-md text-body-md text-on-surface-variant">{t('consulting.services.description')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-6">
              {services.map((service, index) => (
                <article
                  key={service.title}
                  className={`glass-card flex min-h-[260px] flex-col rounded-lg p-8 ${
                    index < 3 ? 'lg:col-span-2' : 'lg:col-span-3'
                  }`}
                >
                  <div className="mb-8 border-b border-white/5 pb-6">
                    <span className="material-symbols-outlined text-[38px] text-cyan-400">{service.icon}</span>
                  </div>
                  <h3 className="font-headline-md text-2xl text-white md:text-[28px]">{service.title}</h3>
                  <p className="mt-4 font-body-md text-body-md text-on-surface-variant">{service.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background py-section-padding">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div>
              <span className="mb-4 block font-label-caps text-label-caps text-cyan-400">{t('consulting.sectors.eyebrow')}</span>
              <h2 className="font-headline-lg text-headline-lg text-white">{t('consulting.sectors.title')}</h2>
              <p className="mt-5 font-body-md text-body-md text-on-surface-variant">{t('consulting.sectors.description')}</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {sectors.map((sector) => (
                <div key={sector} className="flex min-h-[82px] items-center gap-4 border border-white/10 bg-zinc-950/40 p-5">
                  <span className="material-symbols-outlined text-cyan-400">check_circle</span>
                  <span className="font-label-caps text-sm text-white">{sector}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/5 bg-surface-container-lowest py-section-padding">
          <div className="mx-auto max-w-7xl px-6 md:px-8">
            <div className="mb-14 flex flex-col justify-between gap-8 border-b border-white/10 pb-10 lg:flex-row lg:items-end">
              <div className="max-w-3xl">
                <span className="mb-4 block font-label-caps text-label-caps text-cyan-400">{t('consulting.process.eyebrow')}</span>
                <h2 className="font-headline-lg text-headline-lg text-white">{t('consulting.process.title')}</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
              {process.map((step) => (
                <article key={step.title} className="border-l border-cyan-400/40 bg-zinc-950/35 p-7">
                  <h3 className="font-headline-md text-2xl text-white">{step.title}</h3>
                  <p className="mt-4 font-body-md text-body-md text-on-surface-variant">{step.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="contacto" className="relative overflow-hidden bg-background py-section-padding">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 lg:gap-16 px-6 md:px-8 lg:grid-cols-2">
            <div>
              <span className="mb-6 block font-label-caps text-label-caps text-cyan-400">{t('consulting.contact.eyebrow')}</span>
              <h2 className="font-headline-lg text-headline-lg text-white">{t('consulting.contact.title')}</h2>
              <p className="mt-6 max-w-xl font-body-lg text-body-lg text-on-surface-variant">{t('consulting.contact.description')}</p>
              <div className="mt-10 space-y-6">
                <div className="flex items-start gap-4">
                  <span className="material-symbols-outlined text-cyan-400">schedule</span>
                  <p className="font-body-md text-body-md text-zinc-300">{t('consulting.contact.response')}</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="material-symbols-outlined text-cyan-400">mail</span>
                  <p className="font-body-md text-body-md text-zinc-300">{t('landing.email')}</p>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="glass-card space-y-6 rounded-lg p-8 md:p-10">
              <div>
                <label className="mb-2 block font-label-caps text-[10px] text-zinc-400" htmlFor="company">{t('consulting.form.company')}</label>
                <input className="w-full border border-white/10 bg-zinc-950 p-4 text-white transition-colors focus:border-cyan-400 focus:ring-0" type="text" name="company" id="company" value={form.company} onChange={handleChange} placeholder={t('consulting.form.companyPlaceholder')} required />
              </div>
              <div>
                <label className="mb-2 block font-label-caps text-[10px] text-zinc-400" htmlFor="consulting-email">{t('landing.form.email')}</label>
                <input className="w-full border border-white/10 bg-zinc-950 p-4 text-white transition-colors focus:border-cyan-400 focus:ring-0" type="email" name="email" id="consulting-email" value={form.email} onChange={handleChange} placeholder={t('landing.form.emailPlaceholder')} required />
              </div>
              <div>
                <label className="mb-2 block font-label-caps text-[10px] text-zinc-400" htmlFor="challenge">{t('consulting.form.challenge')}</label>
                <textarea className="w-full border border-white/10 bg-zinc-950 p-4 text-white transition-colors focus:border-cyan-400 focus:ring-0" rows="5" name="challenge" id="challenge" value={form.challenge} onChange={handleChange} placeholder={t('consulting.form.challengePlaceholder')} required />
              </div>
              {submitStatus === 'success' && (
                <p className="text-emerald-400 text-sm text-center">{t('consulting.form.success')}</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm text-center">{t('consulting.form.error')}</p>
              )}
              <button className="w-full rounded bg-secondary-container py-5 font-label-caps text-label-caps text-on-secondary-fixed transition-all duration-300 hover:brightness-110 disabled:opacity-50" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('consulting.form.sending') : t('consulting.form.send')}
              </button>
            </form>
          </div>
        </section>
      </main>

      <TechCarousel />

      <footer className="bg-zinc-950 w-full py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-8 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
            <img src="/elmakinatech-icon.svg" alt="" className="h-10 w-auto" />
            <span className="text-lg font-black text-white tracking-tighter">ElmakinaTech</span>
          </div>
          <div className="flex gap-6 md:gap-12">
            <a onClick={(e) => { e.preventDefault(); navigate(prefix + '/', { state: { scrollTo: 'servicios' } }); }} className="text-zinc-500 font-label-caps text-sm uppercase tracking-widest hover:text-cyan-400 transition-all duration-300 cursor-pointer" href={prefix + '/'}>{t('landing.footer.services')}</a>
            <Link className="text-zinc-500 font-label-caps text-sm uppercase tracking-widest hover:text-cyan-400 transition-all duration-300" to={prefix + '/consultoria'}>{t('landing.footer.consulting')}</Link>
            <Link className="text-zinc-500 font-label-caps text-sm uppercase tracking-widest hover:text-cyan-400 transition-all duration-300" to={prefix + '/contacto'}>{t('landing.footer.contact')}</Link>
          </div>
          <div className="flex gap-6">
            <Link className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest hover:text-cyan-400 transition-all" to={prefix + '/privacidad'}>{t('home.footer.privacy')}</Link>
            <Link className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest hover:text-cyan-400 transition-all" to={prefix + '/terminos'}>{t('legal.terms.title')}</Link>
            <Link className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest hover:text-cyan-400 transition-all" to={prefix + '/politica-de-cookies'}>{t('legal.cookies.title')}</Link>
          </div>
          <p className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest">
            {t('landing.footer.copyright')}
          </p>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}

export default Consulting;
